// Retkipäiväkirja: retket, tapahtuma-aikajana ja korjaukset (suunnitelman kohta 11).

import L from "leaflet";
import { useEffect, useRef, useState } from "preact/hooks";

import { clock, day, dayFromIso, durationText, kmValue, points as fmtPoints, weekdayDay } from "../lib/format.js";
import { MODES, modeLabel } from "../lib/modes.js";
import { createBaseLayer } from "../lib/basemap.js";
import { saveTextFile } from "../lib/files.js";
import { groupTimeline, tileGroupText } from "../lib/timeline.js";
import { filterTrips } from "../lib/tripFilter.js";
import { IconBack } from "./icons.jsx";
import { ImportSheet } from "./ImportSheet.jsx";
import { PlayerChip, TripPeople } from "./PlayerViews.jsx";
import { findPlayer } from "../lib/players.js";
import { Sheet } from "./Sheet.jsx";

const cssVar = (name) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();

export function DiaryView({ app }) {
  const { trips, rec } = app;
  const [filters, setFilters] = useState({ text: "", mode: "", period: "all", confirmedOnly: false });
  const [importing, setImporting] = useState(false);
  if (app.openTripId) return <TripDetail app={app} id={app.openTripId} />;

  const shown = trips ? filterTrips(trips, filters) : null;
  const usedModes = trips ? [...new Set(trips.flatMap((t) => t.modes))] : [];
  const filtering = filters.text || filters.mode || filters.period !== "all" || filters.confirmedOnly;

  return (
    <div class="page">
      <div class="page-inner">
        <h1>Päiväkirja</h1>
        <p class="lede">Retket, niiden reitit ja jokaisen pisteen peruste.</p>
        <button class="btn btn-block import-btn" onClick={() => setImporting(true)}>Tuo retki tiedostosta</button>
        {(importing || app.sharedFile) && (
          <ImportSheet
            app={app}
            initial={app.sharedFile}
            onClose={() => {
              setImporting(false);
              app.clearSharedFile();
            }}
          />
        )}

        {rec.outboxCount > 0 && (
          <div class="notice" role="status">
            {rec.outboxCount === 1 ? "Yksi retki odottaa" : `${rec.outboxCount} retkeä odottaa`} lähetystä palvelimelle.{" "}
            <button class="btn btn-quiet" onClick={() => app.syncNow()}>Lähetä nyt</button>
          </div>
        )}

        {app.tripsStatus === "loading" && !trips?.length && <div class="empty" role="status"><b>Ladataan retkiä…</b></div>}
        {app.tripsStatus === "error" && (
          <div class="empty" role="alert">
            <b>{trips?.length ? "Retkilista voi olla vanhentunut" : "Retkiä ei saatu ladattua"}</b>
            {app.loadError || "Palvelimeen ei saada yhteyttä."}{" "}
            <button class="btn btn-quiet" onClick={() => app.refresh()}>Yritä uudelleen</button>
          </div>
        )}
        {app.tripsStatus === "ready" && trips?.length === 0 && (
          <div class="empty">
            <b>Ei vielä retkiä</b>
            Aloita ensimmäinen retki kartalta. Jo lyhyt kävely lähellä kotia kerää ensimmäiset ruudut.
          </div>
        )}
        {trips?.length > 0 && (
          <div class="diary-filters">
            <label class="sr-only" for="trip-search">Hae retkiä</label>
            <input id="trip-search" type="search" placeholder="Hae nimellä, paikalla tai muistiinpanolla" value={filters.text}
              onInput={(e) => setFilters({ ...filters, text: e.currentTarget.value })} />
            <div class="filter-row">
              <label class="sr-only" for="f-period">Ajanjakso</label>
              <select id="f-period" value={filters.period} onChange={(e) => setFilters({ ...filters, period: e.currentTarget.value })}>
                <option value="all">Koko historia</option>
                <option value="30d">30 päivää</option>
                <option value="year">Tämä vuosi</option>
              </select>
              <label class="sr-only" for="f-mode">Kulkutapa</label>
              <select id="f-mode" value={filters.mode} onChange={(e) => setFilters({ ...filters, mode: e.currentTarget.value })}>
                <option value="">Kaikki kulkutavat</option>
                {usedModes.map((m) => <option key={m} value={m}>{modeLabel(m)}</option>)}
              </select>
            </div>
            <label class="check small">
              <input type="checkbox" checked={filters.confirmedOnly} onChange={() => setFilters({ ...filters, confirmedOnly: !filters.confirmedOnly })} />
              <span>Vain retket, joihin liittyy oma vahvistus</span>
            </label>
            {filtering && (
              <p class="s" aria-live="polite">
                {shown.length} / {trips.length} retkeä.{" "}
                <button class="btn btn-quiet" onClick={() => setFilters({ text: "", mode: "", period: "all", confirmedOnly: false })}>Näytä kaikki</button>
              </p>
            )}
          </div>
        )}
        {filtering && shown.length === 0 && <div class="empty"><b>Ei osumia</b>Kokeile toista hakusanaa tai väljempää ajanjaksoa.</div>}
        {shown?.map((t) => (
          <button class="item" key={t.id} onClick={() => app.setOpenTripId(t.id)}>
            <svg class="mark" viewBox="0 0 30 30" aria-hidden="true"><rect x="6" y="6" width="18" height="18" fill="var(--open)" stroke="var(--open-ink)" stroke-width="1.5" /></svg>
            <div>
              <div class="t">{t.name}</div>
              <div class="s">
                {dayFromIso(t.local_day)}, {kmValue(t.eligible_km)}
                {t.status === "recording" ? ", kesken" : ""}
                {t.source === "import" ? ", tuotu" : ""}
              </div>
              {(t.origin_player || t.companions?.length > 0) && (
                <div class="s people-line">
                  {t.origin_player ? "Tallentanut " : "Mukana "}
                  {(t.origin_player ? [t.origin_player] : t.companions).map((id) => {
                    const p = findPlayer(app.players, id);
                    return p ? <span key={id} class="people-tag"><PlayerChip player={p} /> {p.name}</span> : null;
                  })}
                </div>
              )}
              {t.places?.length > 0 && <div class="s places">{t.places.join(", ")}</div>}
            </div>
            <div class="end"><b>{t.points}</b><br />{t.new_tiles} uutta</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function TripMap({ trip, basemap }) {
  const el = useRef(null);
  useEffect(() => {
    const m = L.map(el.current, { zoomControl: false, attributionControl: false, dragging: true });
    createBaseLayer(basemap).addTo(m);
    const bounds = L.latLngBounds([]);
    for (const seg of trip.segments) {
      if (seg.coords.length < 2) continue;
      const line = L.polyline(seg.coords, seg.eligible
        ? { color: cssVar("--ink"), weight: 4 }
        : { color: cssVar("--ink-soft"), weight: 3, dashArray: "6 6" }).addTo(m);
      bounds.extend(line.getBounds());
    }
    if (bounds.isValid()) m.fitBounds(bounds, { padding: [20, 20] });
    else m.setView([61.058, 28.186], 12);
    return () => m.remove();
  }, [trip.id, trip.segments, basemap]);
  return <div class="trip-map" ref={el} role="img" aria-label="Retken reitti kartalla. Yhtenäinen viiva on lihasvoimaa, katkoviiva siirtymää." />;
}

function TripDetail({ app, id }) {
  const [trip, setTrip] = useState(null);
  const [error, setError] = useState(null);
  const [editModes, setEditModes] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [renaming, setRenaming] = useState(null); // muokattava nimi tai null

  const load = () => app.api.trip(id).then(setTrip, (e) => setError(e.message));
  useEffect(() => { load(); }, [id, app.dataVersion]);

  const back = () => app.setOpenTripId(null);

  // Katkoksen kohta avataan kartalle ruudun lisäystilaan, katkoksen keskelle.
  const openGap = (fromT) => {
    const gap = trip.gaps.find((g) => g.from_t === fromT);
    if (!gap) return;
    const center = [(gap.from_lat + gap.to_lat) / 2, (gap.from_lon + gap.to_lon) / 2];
    app.startTool({ type: "tile", key: null, tripId: trip.id, center, doneOn: trip.events.find((ev) => ev.t === fromT)?.local_day });
  };

  const exportGpx = async () => {
    try {
      await saveTextFile(`${trip.name.replace(/[^\p{L}\p{N} .-]/gu, "")}.gpx`, await app.api.exportGpx(trip.id), "application/gpx+xml");
    } catch (e) {
      app.toast({ kind: "info", what: "Vientiä ei tehty", sub: e.message });
    }
  };

  if (error) return <div class="page"><div class="page-inner"><button class="btn btn-quiet" onClick={back}><IconBack /> Päiväkirja</button><div class="empty"><b>Retkeä ei voitu avata</b>{error}</div></div></div>;
  if (!trip) return <div class="page"><div class="page-inner"><p class="s">Ladataan retkeä…</p></div></div>;

  // Aikajana ryhmitellään paikallisen päivän mukaan; keskiyön ylittävä retki näyttää kaksi päivää.
  let lastDay = null;
  const rows = [];
  const dayMark = (localDay, t) => {
    if (localDay === lastDay) return;
    rows.push(<li class="daymark" key={`d-${localDay}`}>{weekdayDay(t)}</li>);
    lastDay = localDay;
  };
  const visible = trip.events.filter((e) => !(e.type === "mode" && e.t === trip.started_at)); // aloituskulkutapa näkyy aloitusrivillä
  for (const item of groupTimeline(visible)) {
    if (item.kind === "tiles") {
      dayMark(item.localDay, item.fromT);
      const range = clock(item.fromT) === clock(item.toT) ? clock(item.fromT) : `${clock(item.fromT)}–${clock(item.toT)}`;
      rows.push(
        <li key={`tiles-${item.fromT}`}>
          <time class="num">{clock(item.fromT)}</time>
          <i class={`node ${item.small ? "tile" : "bigtile"}`} aria-hidden="true" />
          <div>
            {tileGroupText(item)}
            {range.includes("–") && <div class="muted">Klo {range}</div>}
          </div>
          <span class="pts">{fmtPoints(item.points)}</span>
        </li>,
      );
      continue;
    }
    const e = item.event;
    dayMark(e.local_day, e.t);
    const startMode = e.type === "start" ? `, ${modeLabel(trip.modes[0]?.mode).toLowerCase()}` : "";
    rows.push(
      <li key={`${e.type}-${e.t}-${e.key ?? ""}`}>
        <time class="num">{clock(e.t)}</time>
        <i class={`node ${e.type}`} aria-hidden="true" />
        <div>
          {e.text}{startMode}
          {e.type === "gap" && (
            <>
              <div class="muted">Noin {Math.round(e.minutes)} min ilman sijaintia. Väliä ei arvata. Puuttuvan kohteen tai ruudun voi merkitä itse.</div>
              <button class="btn gap-action" onClick={() => openGap(e.t)}>Lisää ruudut kartalla</button>
            </>
          )}
        </div>
        <span class="pts">{e.points ? fmtPoints(e.points) : ""}</span>
      </li>,
    );
  }

  return (
    <div class="page">
      <div class="page-inner">
        <button class="btn btn-quiet" onClick={back} style={{ marginLeft: -10 }}><IconBack /> Päiväkirja</button>
        <h1>{trip.name}</h1>
        <p class="lede">
          {day(trip.started_at)}
          {trip.status === "recording" ? ", tallennus kesken tai keskeytynyt" : ""}
          {trip.source === "import" ? `, tuotu tiedostosta ${trip.source_file}` : ""}
        </p>

        {trip.overlaps?.length > 0 && (
          <div class="notice warn">
            Samaan aikaan on toinenkin retki: {trip.overlaps.map((o) => o.name || "nimetön").join(", ")}. Jos sama lenkki
            on tallennettu kahdesti (esim. tämä sovellus ja urheilusovellus), poista toinen, ettei matkaa lasketa kahdesti.
            Ruudut ja pisteet säilyvät, koska ne ovat myös toisella retkellä.{" "}
            <button class="btn btn-quiet" onClick={() => app.openTrip(trip.overlaps[0].id)}>Avaa toinen retki</button>
          </div>
        )}

        <TripPeople app={app} trip={trip} />

        <TripMap trip={trip} basemap={app.basemap} />

        <div class="figures">
          <div><b>{kmValue(trip.eligible_km).replace(" km", "")}</b><span>km lihasvoimalla</span></div>
          <div><b>{trip.points}</b><span>pistettä</span></div>
          <div><b>{trip.events.filter((e) => e.type === "tile").length}</b><span>uutta ruutua</span></div>
        </div>
        <p class="s">
          Liikkuminen arviolta {durationText(trip.moving_minutes_estimate * 60)}.
          {trip.transfer_km > 0 && ` Siirtymää ${kmValue(trip.transfer_km)}, ei kerrytä peliä.`}
          {trip.paused_minutes > 0 && ` Taukoja ${durationText(trip.paused_minutes * 60)}.`}
          {trip.revisited_tiles > 0 && ` Tuttuja ruutuja ${trip.revisited_tiles}.`}
        </p>

        <h2>Kulkutavat</h2>
        {trip.modes.map((m, i) => (
          <p class="s" key={m.start_t}>{i === 0 ? "Alusta" : `Klo ${clock(m.start_t)} alkaen`}: <b>{modeLabel(m.mode)}</b></p>
        ))}
        {!trip.origin_player && <button class="btn" onClick={() => setEditModes(trip.modes.map((m) => ({ ...m })))}>Korjaa kulkutavat</button>}

        <h2>Tapahtumat</h2>
        <ol class="timeline">{rows}</ol>

        <h2>Retken hallinta</h2>
        <div class="sheet-actions" style={{ marginTop: 0 }}>
          {!trip.origin_player && <button class="btn" onClick={() => setRenaming(trip.name)}>Nimeä retki</button>}
          <button class="btn" onClick={exportGpx}>Vie GPX-tiedostona</button>
          {!trip.origin_player && <button class="btn btn-danger" onClick={() => setConfirmDelete(true)}>Poista retki</button>}
        </div>
      </div>

      {editModes && (
        <Sheet title="Korjaa kulkutavat" onClose={() => setEditModes(null)}>
          <p class="hint">GPS-jälki pysyy ennallaan. Pisteet lasketaan uudelleen, ja muutokset näkyvät pistehistoriassa oikaisuina.</p>
          {editModes.map((m, i) => (
            <div class="field" key={i}>
              <label for={`mode-${i}`}>{i === 0 ? "Alusta" : `Klo ${clock(m.start_t)} alkaen`}</label>
              <select id={`mode-${i}`} value={m.mode} onChange={(e) => setEditModes(editModes.map((x, j) => (j === i ? { ...x, mode: e.currentTarget.value } : x)))}>
                {MODES.map((opt) => <option key={opt.id} value={opt.id}>{opt.label}{opt.eligible ? "" : " (siirtymä)"}</option>)}
              </select>
            </div>
          ))}
          <div class="sheet-actions">
            <button class="btn btn-primary btn-block" onClick={async () => {
              const ok = await app.replaceModes(trip.id, editModes);
              if (ok) { setEditModes(null); load(); }
            }}>Tallenna korjaus</button>
            <button class="btn btn-block" onClick={() => setEditModes(null)}>Peruuta</button>
          </div>
        </Sheet>
      )}

      {renaming !== null && (
        <Sheet title="Nimeä retki" onClose={() => setRenaming(null)}>
          <form onSubmit={async (e) => {
            e.preventDefault();
            const name = renaming.trim();
            if (!name) return;
            const ok = await app.renameTrip(trip.id, name);
            if (ok) { setRenaming(null); load(); }
          }}>
            <div class="field">
              <label for="trip-name">Nimi</label>
              <input id="trip-name" value={renaming} maxLength={120} onInput={(e) => setRenaming(e.currentTarget.value)} />
            </div>
            <div class="sheet-actions">
              <button class="btn btn-primary btn-block" type="submit" disabled={!renaming.trim()}>Tallenna nimi</button>
              <button class="btn btn-block" type="button" onClick={() => setRenaming(null)}>Peruuta</button>
            </div>
          </form>
        </Sheet>
      )}

      {confirmDelete && (
        <Sheet title="Poistetaanko retki?" onClose={() => setConfirmDelete(false)}>
          <p class="hint">Reittijälki poistuu pysyvästi. Ruudut ja saavutukset säilyvät, jos ne on saavutettu myös toisella retkellä tai omalla merkinnällä. Muuten niistä kirjataan näkyvä oikaisu.</p>
          <div class="sheet-actions">
            <button class="btn btn-danger btn-block" onClick={async () => {
              const ok = await app.deleteTrip(trip.id);
              if (ok) { setConfirmDelete(false); back(); }
            }}>Poista retki</button>
            <button class="btn btn-block" onClick={() => setConfirmDelete(false)}>Säilytä</button>
          </div>
        </Sheet>
      )}
    </div>
  );
}
